import { Button, Card, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import { CardLarge, PageHeader } from '../../app/AppStyles';
import { fetchPerformance } from '../register/registerSlice';
import ContentLoader from '../subcomponents/ContentLoader';
import PageLayout from '../subcomponents/PageLayout';
import MolliePaymentButton from './MolliePaymentButton';
import { fetchPartialPaymentInfo, fetchTransactionByPaymentId, putPartialPaymentReservation, resetMollieFetchResults } from './mollieSlice';

import HomeIcon from '@mui/icons-material/Home';

export default function MollieReturnPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { id } = useParams();
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { siomSettings } = useSelector((state) => state.settings.siom);
    const { performance, performanceStatus } = useSelector((state) => state.register);
    const { transaction, transactionStatus, partialPaymentInfo, partialPaymentInfoStatus, confirmPartialPaymentReservationStatus } = useSelector((state) => state.mollie);

    // Reset Mollie fetch results
    useEffect(() => {
        dispatch(resetMollieFetchResults());
    }, []);

    // Fetch ideal transaction from DB
    useEffect(() => {
        dispatch(fetchTransactionByPaymentId(id));
    }, [id]);

    // Fetch performance info
    useEffect(() => {
        if (transactionStatus === "success")
            dispatch(fetchPerformance({ performanceId: transaction?.onderwijsUitvoeringId }));
    }, [transactionStatus]);

    // Fetch partial payment info
    useEffect(() => {
        if (transactionStatus === "success" && transaction?.isDeelbetaling === true) {
            dispatch(fetchPartialPaymentInfo({
                subscriptionId: transaction?.inschrijvingId,
                performanceId: transaction?.onderwijsUitvoeringId,
                studentId: transaction?.studentId
            }));
        }
    }, [transactionStatus]);

    // Update partial payment reservation info if not done yet (to show updated info)
    useEffect(() => {
        if (transaction?.isDeelbetaling === true && partialPaymentInfoStatus === 'success') {
            dispatch(putPartialPaymentReservation({
                performanceId: transaction?.onderwijsUitvoeringId,
                subscriptionId: transaction?.inschrijvingId,
                studentId: transaction?.studentId
            }));
        }
    }, [partialPaymentInfoStatus])

    return (
        <PageLayout
            title={resourceFinal[appLanguage].words.PAYMENT}
            titleLower={resourceFinal[appLanguage].words.RESULT}
            content={
                <ContentLoader
                    successCondition={transactionStatus === "success" && performanceStatus === "success"}
                    errorCondition={transactionStatus === "failed"}
                    content={
                        <>
                            <Card sx={{ ...CardLarge, flex: 0, p: 1 }}>
                                <Typography variant="h6" textAlign="center" sx={{ ...PageHeader }}>{performance?.activity.name}{performance?.performanceName && ` - ${performance?.performanceName}`}</Typography>
                                <Typography variant="h6" textAlign="center">{resourceFinal[appLanguage].words.AMOUNT}: {Intl.NumberFormat('nl-NL', { style: "currency", currency: "EUR", }).format(transaction?.bedrag)}</Typography>
                                <Typography variant="h6" textAlign="center">{resourceFinal[appLanguage].words.STATE}: {transaction?.status}</Typography>
                            </Card>
                            <Card sx={{ ...CardLarge, p: 1, mt: 1 }}>
                                {transaction?.isDeelbetaling === false &&
                                    <>
                                        {transaction?.status?.toLowerCase() === "paid" &&
                                            <Typography variant="h6" textAlign="center">
                                                {resourceFinal[appLanguage].messages.PAYMENT_PAID}
                                            </Typography>}
                                        {transaction?.status?.toLowerCase() === "expired" &&
                                            <Typography variant="h6" textAlign="center">
                                                {resourceFinal[appLanguage].messages.PAYMENT_EXPIRED}
                                            </Typography>}
                                        {transaction?.status?.toLowerCase() === "failed" &&
                                            <Typography variant="h6" textAlign="center">
                                                {resourceFinal[appLanguage].messages.PAYMENT_FAILED}
                                            </Typography>}
                                        {transaction?.status?.toLowerCase() === "canceled" &&
                                            <Typography variant="h6" textAlign="center">
                                                {resourceFinal[appLanguage].messages.PAYMENT_CANCELED}
                                            </Typography>}
                                        {(transaction?.status?.toLowerCase() !== "paid" && transaction?.status !== "open" && !siomSettings.mollieEnableDelay) &&
                                            <Typography variant="h6" textAlign="center">
                                                {resourceFinal[appLanguage].messages.PAYMENT_ACTION1}
                                            </Typography>}
                                        {(transaction?.status?.toLowerCase() === "open" || (transaction?.status?.toLowerCase() !== "paid" && transaction?.status?.toLowerCase() !== "open" && siomSettings.mollieEnableDelay)) && <>
                                        <MolliePaymentButton buttonMode="mollieReturn" performanceId={performance?.performanceId} transactionId={transaction?.transactieId} subscriptionId={transaction?.inschrijvingId} />
                                        </>}
                                    </>
                                }
                                {transaction?.isDeelbetaling === true &&
                                    <>
                                        <ContentLoader
                                            successCondition={partialPaymentInfoStatus === 'success'}
                                            errorCondition={partialPaymentInfoStatus === 'failed'}
                                            errorCompact
                                            content={
                                                <>
                                                    <Typography variant="h6" align="center" gutterBottom>
                                                        {resourceFinal[appLanguage].words.PARTIAL_PAYMENTS} {resourceFinal[appLanguage].words.PARTIAL_PAYMENT_COMPLETED.toLowerCase()}: {partialPaymentInfo?.partialPaymentCompleted}/{partialPaymentInfo?.partialPaymentAmount}
                                                    </Typography>

                                                    {partialPaymentInfo?.allPartialPaymentsCompleted &&
                                                        <ContentLoader
                                                            successCondition={confirmPartialPaymentReservationStatus === 'success'}
                                                            errorCondition={confirmPartialPaymentReservationStatus === 'failed'}
                                                            errorCompact
                                                            content={
                                                                <Typography variant="h6" align="center" gutterBottom>
                                                                    {resourceFinal[appLanguage].messages.PAYMENT_PARTIAL_RESERVATION_UPDATED}
                                                                </Typography>
                                                            }
                                                        />
                                                    }
                                                </>
                                            }
                                        />
                                    </>
                                }
                                <Button variant="outlined" color="secondary" sx={{ width: 110, alignSelf: 'center', mt: 'auto' }}
                                    startIcon={<HomeIcon />} onClick={() => navigate("/")}>
                                    {resourceFinal[appLanguage].words.HOME}
                                </Button>
                            </Card>
                        </>
                    }
                />
            }
            bottomBar
            disableContentBackground
        />
    );
}
